import React, { useEffect, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { CheckoutForm, CheckoutFormData } from '../components/checkout/CheckoutForm';
import { useCart } from '../lib/cart';
import { useTableOrder } from '../lib/table-context';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase';
import { DELIVERY_TIME_SLOTS } from '../lib/preorder';
import { formatCurrency, aggregateQuantityOptions } from '../lib/utils';
import { ArrowLeft, Store, ShoppingBag, AlertTriangle, ExternalLink } from 'lucide-react';
import { EmptyState } from '../components/ui/EmptyState';
import { Restaurant, RestaurantSettings } from '../lib/types';
import { useKitchenPause, checkKitchenPausedBeforeOrder } from '../lib/kitchen-pause-context';
import { useRestaurantScheduleStatus, isRestaurantClosedForOrdering } from '../lib/schedule';
import { getCustomerByPhone, upsertCustomerProfile, saveCustomerToLocal, getCustomerFromLocal } from '../lib/customer';
// ─── Order Intent ID helpers ─────────────────────────────────
function getOrCreateOrderIntentId(restaurantId: number): string {
  const storageKey = `order_intent_id:${restaurantId}`;
  const existing = sessionStorage.getItem(storageKey);
  if (existing) {
    console.log('[CHECKOUT] Reusing existing order_intent_id:', existing);
    return existing;
  }
  const created = crypto.randomUUID();
  sessionStorage.setItem(storageKey, created);
  console.log('[CHECKOUT] Generated new order_intent_id:', created);
  return created;
}
function clearOrderIntentId(restaurantId: number): void {
  const storageKey = `order_intent_id:${restaurantId}`;
  sessionStorage.removeItem(storageKey);
  console.log('[CHECKOUT] Cleared order_intent_id for restaurant:', restaurantId);
}
function generateFreshOrderIntentId(restaurantId: number): string {
  const storageKey = `order_intent_id:${restaurantId}`;
  const fresh = crypto.randomUUID();
  sessionStorage.setItem(storageKey, fresh);
  console.log('[CHECKOUT] Generated FRESH order_intent_id for force_new_order:', fresh);
  return fresh;
}
// ─── Recent Order Info type ──────────────────────────────────
interface RecentOrderInfo {
  id: string;
  order_number: string;
  tracking_token?: string;
  tracking_link?: string;
  status: string;
  created_at: string;
  total: number;
  message: string;
}
// ─── Page Component ──────────────────────────────────────────
export function CheckoutPage() {
  const navigate = useNavigate();
  const {
    cart,
    getTotal,
    clearCart,
    getDiscount
  } = useCart();
  const {
    tableOrder,
    isTableOrder,
    clearTableOrder
  } = useTableOrder();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  // Simplified QR checkout (table order behavior) is opt-in per restaurant.
  // Currently enabled for NatuCafé (id 19) and Rancho San Francisco (id 28).
  // For every other restaurant we treat the checkout as a normal marketplace
  // order, even if a stale tableOrder lingers in localStorage from a previous
  // QR scan.
  const isQrTableForThisRestaurant = isTableOrder && !!tableOrder && !!cart.restaurantId && String(tableOrder.restaurantId) === String(cart.restaurantId);
  const useSimplifiedQrCheckout = isQrTableForThisRestaurant && (Number(cart.restaurantId) === 19 || Number(cart.restaurantId) === 28);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [settings, setSettings] = useState<RestaurantSettings | null>(null);
  const [savedCustomer, setSavedCustomer] = useState<{
    customer_name: string;
    customer_phone: string;
    delivery_address: string;
    maps_url: string;
  } | null>(null);
  // Recent order confirmation guard
  const [recentOrderInfo, setRecentOrderInfo] = useState<RecentOrderInfo | null>(null);
  const [showRecentOrderModal, setShowRecentOrderModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const [pendingFormData, setPendingFormData] = useState<CheckoutFormData | null>(null);
  const [isResending, setIsResending] = useState(false);
  // Hard failure modal — when create-order fails entirely, push the
  // customer to contact the restaurant directly via WhatsApp.
  const [hardFailureMessage, setHardFailureMessage] = useState<string | null>(null);
  // Guard against concurrent submissions
  const submittingRef = useRef(false);
  // Schedule-based open/closed enforcement (declared AFTER restaurant state
  // exists so we never reference it before initialization).
  const scheduleStatus = useRestaurantScheduleStatus(restaurant?.slug || cart.restaurantSlug);
  const isClosedBySchedule = scheduleStatus.isClosedForOrdering;
  // Load saved customer from Supabase (with localStorage fallback)
  useEffect(() => {
    const loadSavedCustomer = async () => {
      try {
        const savedPhone = localStorage.getItem('noiza_customer_phone');
        if (!savedPhone) return;
        // Try Supabase first
        const customer = await getCustomerByPhone(savedPhone);
        if (customer) {
          setSavedCustomer({
            customer_name: customer.full_name || '',
            customer_phone: customer.phone || '',
            delivery_address: customer.address_text || '',
            maps_url: customer.maps_url || ''
          });
          return;
        }
        // Fallback: load from localStorage
        const localCustomer = getCustomerFromLocal();
        if (localCustomer) {
          setSavedCustomer({
            customer_name: localCustomer.full_name || '',
            customer_phone: localCustomer.phone || '',
            delivery_address: localCustomer.address_text || '',
            maps_url: localCustomer.maps_url || ''
          });
        }
      } catch (error) {
        console.error('[CHECKOUT] loadSavedCustomer Supabase error:', error);
        // Fallback: load from localStorage even on error
        const localCustomer = getCustomerFromLocal();
        if (localCustomer) {
          setSavedCustomer({
            customer_name: localCustomer.full_name || '',
            customer_phone: localCustomer.phone || '',
            delivery_address: localCustomer.address_text || '',
            maps_url: localCustomer.maps_url || ''
          });
        }
      }
    };
    loadSavedCustomer();
  }, []);
  useEffect(() => {
    if (!cart.restaurantId) return;
    const restaurantId = cart.restaurantId;
    console.log('[NOIZA][CheckoutPage] Registering restaurant:', restaurantId);
    monitorRestaurant(restaurantId);
    supabase.from('restaurants').select('*').eq('id', restaurantId).single().then(({
      data
    }) => {
      if (data) setRestaurant(data);
    });
    supabase.from('restaurant_settings').select('*').eq('restaurant_id', restaurantId).single().then(({
      data
    }) => {
      if (data) setSettings(data);
    });
  }, [cart.restaurantId, monitorRestaurant]);
  const subtotal = getTotal();
  const discount = getDiscount();
  // Crack Pollo: per-zone fee set by customer in CheckoutForm. For every
  // other restaurant we keep using restaurant.express_fee as before.
  const isCrackPolloRestaurant = restaurant?.slug === 'crack-pollo';
  const [crackPolloDeliveryFee, setCrackPolloDeliveryFee] = useState<number>(0);
  // MS Express external delivery — gated behind uses_external_delivery. When
  // on, the delivery fee comes from the zone/subzone the customer picked in
  // CheckoutForm; otherwise this is inert and the existing flow is unchanged.
  const usesExternalDelivery = settings?.uses_external_delivery === true;
  const isEmilius = Number(restaurant?.id) === 27 || restaurant?.slug === 'emilius-pizza';
  const [externalDelivery, setExternalDelivery] = useState<{
    fee: number;
    zone: string;
    subzone: string;
    catalogId: string | null;
    confirmationPending?: boolean;
  } | null>(null);
  const isEmiliusDeliveryConfirmationPending = isEmilius && externalDelivery?.confirmationPending === true;
  const deliveryFee = usesExternalDelivery || isEmilius ? externalDelivery?.fee || 0 : isCrackPolloRestaurant ? crackPolloDeliveryFee : restaurant?.express_fee || 0;
  // Food subtotal is always the cart subtotal (delivery never mixes in).
  const foodSubtotal = subtotal;
  const total = foodSubtotal + deliveryFee;
  const extractLatLngFromMapsUrl = (url: string) => {
    if (!url) return {
      lat: null,
      lng: null
    };
    const match = url.match(/q=([-\d.]+),([-\d.]+)/);
    if (!match) return {
      lat: null,
      lng: null
    };
    return {
      lat: Number(match[1]),
      lng: Number(match[2])
    };
  };
  // ─── Shared function to call create-order ──────────────────
  const callCreateOrder = async (payload: any): Promise<{
    response: Response;
    result: any;
  }> => {
    console.log('[CHECKOUT] Sending create-order payload:', JSON.stringify(payload).substring(0, 500));
    const response = await fetch(`${SUPABASE_URL}/functions/v1/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    console.log('[CHECKOUT] create-order response:', result);
    return {
      response,
      result
    };
  };
  // ─── Handle successful order ───────────────────────────────
  const handleOrderSuccess = async (result: any, formData: CheckoutFormData, actualDeliveryFee: number, orderTotal: number, restaurantIdNum: number) => {
    const order = result.order || result;
    const isDuplicate = result.duplicate_prevented === true;
    if (isDuplicate) {
      console.log('[CHECKOUT] duplicate_prevented=true — order already exists:', order.order_number);
    } else {
      console.log('[CHECKOUT] Order created successfully:', order.order_number);
    }
    const trackingToken = order.tracking_token;
    const orderNumber = order.order_number;
    if (!orderNumber || !trackingToken) {
      console.error('[CHECKOUT] Missing order_number or tracking_token in response:', result);
      throw new Error('Respuesta incompleta del servidor. Contacta al restaurante.');
    }
    const orderDeliveryType = useSimplifiedQrCheckout ? 'dine_in' : formData.delivery_type;
    // ── Generate and upload ticket image (non-blocking) ──
    const itemsSnapshot = [...cart.items];
    let ticketImageUrl: string | null = null;
    if (!formData.delivery_confirmation_pending) {
      try {
        const {
          generateTicketImage,
          uploadTicketToCloudinary
        } = await import('../lib/ticket');
        const ticketBlob = await generateTicketImage({
          restaurantName: restaurant?.name || '',
          currencySymbol: restaurant?.currency_symbol || '₡',
          orderNumber,
          customerName: formData.customer_name,
          customerPhone: formData.customer_phone,
          deliveryType: formData.delivery_type,
          deliveryAddress: formData.delivery_address,
          mapsUrl: formData.maps_url,
          items: itemsSnapshot,
          subtotal,
          deliveryFee: actualDeliveryFee,
          total: orderTotal,
          notes: formData.notes,
          createdAt: order.created_at || new Date().toISOString(),
          deliveryZone: orderDeliveryType === 'delivery' ? formData.delivery_zone || null : null,
          deliverySubzone: orderDeliveryType === 'delivery' ? formData.delivery_subzone || null : null
        });
        const url = await uploadTicketToCloudinary(ticketBlob, orderNumber);
        if (url) {
          ticketImageUrl = url;
          if (!isDuplicate) {
            await supabase.from('orders').update({
              ticket_image_url: url
            }).eq('id', order.id).then(({
              error
            }) => {
              if (error) console.error('[CHECKOUT] ticket_image_url update error:', error);
            });
          }
        }
      } catch (ticketErr) {
        console.error('[CHECKOUT] Ticket generation/upload error:', ticketErr);
      }
    }
    // ── Success! Clear state and redirect ──
    clearOrderIntentId(restaurantIdNum);
    console.log('[CHECKOUT] Order intent cleared');
    clearCart();
    if (useSimplifiedQrCheckout) clearTableOrder();
    console.log('[CHECKOUT] Cart cleared');
    console.log('[CHECKOUT] Navigating to order-confirmation');
    navigate('/order-confirmation', {
      state: {
        restaurant,
        orderNumber,
        trackingToken,
        orderId: order.id,
        customerName: formData.customer_name,
        customerPhone: formData.customer_phone,
        deliveryType: orderDeliveryType,
        paymentMethod: formData.payment_method,
        deliveryAddress: formData.delivery_address,
        mapsUrl: formData.maps_url,
        notes: formData.notes,
        items: itemsSnapshot,
        subtotal,
        deliveryFee: actualDeliveryFee,
        total: orderTotal,
        ticketImageUrl,
        customerTrackingVisible: settings?.customer_tracking_visible ?? true,
        duplicatePrevented: isDuplicate,
        preorderDate: formData.preorder_date || null,
        preorderTimeSlot: formData.preorder_time_slot || null,
        paymentProofUrl: formData.payment_proof_url || null,
        deliveryConfirmationPending: formData.delivery_confirmation_pending === true,
        // MS Express external delivery context for the WhatsApp message
        usesExternalDelivery,
        isEmilius,
        deliveryZone: orderDeliveryType === 'delivery' ? formData.delivery_zone || null : null,
        deliverySubzone: orderDeliveryType === 'delivery' ? formData.delivery_subzone || null : null,
        foodSubtotal: subtotal
      }
    });
  };
  // ─── Build payload ─────────────────────────────────────────
  const buildPayload = (data: CheckoutFormData, orderIntentId: string, forceNewOrder: boolean) => {
    const {
      lat: extractedLat,
      lng: extractedLng
    } = extractLatLngFromMapsUrl(data.maps_url);
    const actualDeliveryFee = data.delivery_type === 'delivery' ? usesExternalDelivery || isEmilius ? data.external_delivery_fee ?? deliveryFee : deliveryFee : 0;
    const orderTotal = subtotal + actualDeliveryFee;
    const resolvedTableId = useSimplifiedQrCheckout && tableOrder ? tableOrder.tableId : data.table_id?.trim() || null;
    const sanitizedTableId = resolvedTableId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(resolvedTableId) ? resolvedTableId : null;
    const orderSource = useSimplifiedQrCheckout ? 'qr' : 'web';
    const orderDeliveryType = useSimplifiedQrCheckout ? 'dine_in' : data.delivery_type;
    // Embed preorder info in notes for visibility
    let finalNotes = data.notes || '';
    if (data.preorder_date && data.preorder_time_slot) {
      const slotLabel = DELIVERY_TIME_SLOTS.find(s => s.id === data.preorder_time_slot)?.label || data.preorder_time_slot;
      const preorderInfo = `📦 PREORDER — Entrega: ${data.preorder_date} · Horario: ${slotLabel}`;
      finalNotes = finalNotes ? `${preorderInfo}\n${finalNotes}` : preorderInfo;
    }
    // ─── CRM / email marketing — ALWAYS persist customer email ─────
    // The customer's email is captured in CheckoutForm and MUST be
    // persisted on every order so it's available for CRM and email
    // marketing flows downstream, regardless of whether the customer
    // requested a fiscal invoice or whether Resend succeeded.
    //
    // We populate BOTH columns intentionally so the data shape is
    // predictable for any future query:
    //   - orders.customer_email  → general contact (CRM primary)
    //   - orders.invoice_email   → email to send the invoice to. If
    //     the customer didn't fill the dedicated invoice_email field,
    //     we fall back to customer_email so the invoice column is
    //     never null when there's any email available.
    //
    // Privacy: both columns live on `orders` and are scoped by
    // restaurant_id — RLS on orders already isolates them per tenant.
    const customerEmailTrimmed = data.customer_email?.trim() || null;
    const dedicatedInvoiceEmailTrimmed = data.invoice_email?.trim() || null;
    const invoiceEmailPersisted = dedicatedInvoiceEmailTrimmed || customerEmailTrimmed;
    return {
      order_intent_id: orderIntentId,
      force_new_order: forceNewOrder,
      restaurant_id: Number(cart.restaurantId),
      customer_name: data.customer_name,
      customer_phone: data.customer_phone || null,
      customer_email: customerEmailTrimmed,
      delivery_type: orderDeliveryType,
      payment_method: data.payment_method,
      delivery_address: data.delivery_address || null,
      delivery_lat: data.delivery_lat || extractedLat || null,
      delivery_lng: data.delivery_lng || extractedLng || null,
      maps_url: data.maps_url || null,
      table_id: sanitizedTableId,
      notes: finalNotes || null,
      subtotal,
      delivery_fee: actualDeliveryFee,
      total: orderTotal,
      // MS Express external delivery — separate food subtotal from the
      // delivery fee. These piggy-back on every payload; create-order only
      // persists them when present, so non-external-delivery orders are
      // unaffected (food_subtotal mirrors subtotal, grand_total mirrors total).
      food_subtotal: subtotal,
      grand_total: orderTotal,
      delivery_zone: data.delivery_type === 'delivery' ? data.delivery_zone || null : null,
      delivery_subzone: data.delivery_type === 'delivery' ? data.delivery_subzone || null : null,
      delivery_zone_catalog_id: data.delivery_type === 'delivery' ? data.delivery_zone_catalog_id || null : null,
      source: orderSource,
      delivery_date: data.preorder_date || null,
      delivery_time_slot: data.preorder_time_slot || null,
      // Invoice request fields — sent on every order. wants_invoice
      // is true only if the customer ticked the checkbox; the email
      // and other fields piggy-back on the order regardless so
      // InvoiceRequestsPage / send-invoice-email can resolve them
      // straight from the DB without round-tripping React state.
      wants_invoice: data.wants_invoice === true,
      invoice_customer_name: data.invoice_customer_name?.trim() || null,
      invoice_legal_id: data.invoice_legal_id?.trim() || null,
      invoice_email: invoiceEmailPersisted,
      // Best-effort: surface the Cloudinary proof URL when present. The
      // create-order function can persist it if a column exists, otherwise
      // it'll be ignored on the server. The URL also lives inside notes,
      // so no information is lost.
      payment_proof_url: data.payment_proof_url || null,
      delivery_confirmation_pending: data.delivery_confirmation_pending === true,
      items: cart.items.map(item => ({
        menu_item_id: item.menu_item_id,
        item_name_snapshot: item.item_name_snapshot,
        unit_price_snapshot: item.unit_price_snapshot,
        quantity: item.quantity,
        notes: item.notes || null,
        selected_options: item.selected_options.map(opt => ({
          option_group: opt.option_group || '',
          option_name: opt.option_name,
          price_delta: opt.price_delta
        }))
      }))
    };
  };
  const handleSubmit = async (data: CheckoutFormData) => {
    if (!cart.restaurantId) return;
    // ── Double-submit guard (ref-based, survives re-renders) ──
    if (submittingRef.current) {
      console.warn('[CHECKOUT] Submission already in flight — ignoring duplicate click');
      return;
    }
    // ── Kitchen pause guards ──
    if (isKitchenPaused) {
      alert('La cocina está pausada. No podemos recibir pedidos. Vuelve en 10 minutos.');
      return;
    }
    // ── Schedule guard (also re-check via stateless helper for safety) ──
    const slugForCheck = restaurant?.slug || cart.restaurantSlug;
    if (isClosedBySchedule || isRestaurantClosedForOrdering(slugForCheck)) {
      alert(`${restaurant?.name || 'El restaurante'} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
      return;
    }
    const pauseCheck = await checkKitchenPausedBeforeOrder(cart.restaurantId);
    if (!pauseCheck.allowed) {
      alert(pauseCheck.error || 'La cocina está pausada.');
      return;
    }
    // ── Lock UI immediately ──
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    console.log('[CHECKOUT] Submit started');
    // ── Save/update customer profile ──
    const {
      lat: extractedLat,
      lng: extractedLng
    } = extractLatLngFromMapsUrl(data.maps_url);
    // Always save to localStorage (robust fallback regardless of Supabase RLS)
    saveCustomerToLocal({
      full_name: data.customer_name,
      phone: data.customer_phone.replace(/[^\d]/g, ''),
      email: data.customer_email || undefined,
      address_text: data.delivery_address || undefined,
      maps_url: data.maps_url || undefined,
      terms_accepted: true
    });
    try {
      await upsertCustomerProfile({
        fullName: data.customer_name,
        phone: data.customer_phone,
        email: data.customer_email || undefined,
        addressText: data.delivery_address,
        mapsUrl: data.maps_url,
        lat: data.delivery_lat || extractedLat,
        lng: data.delivery_lng || extractedLng,
        termsAccepted: true
      });
    } catch (error) {
      console.error('[CHECKOUT] upsertCustomerProfile error (localStorage fallback active):', error);
    }
    try {
      const restaurantIdNum = Number(cart.restaurantId);
      const orderIntentId = getOrCreateOrderIntentId(restaurantIdNum);
      console.log('[CHECKOUT] order_intent_id:', orderIntentId);
      const payload = buildPayload(data, orderIntentId, false);
      const {
        response,
        result
      } = await callCreateOrder(payload);
      // ── Check for recent order guard (HTTP 409) ──
      if (response.status === 409 && result?.recent_order_detected && result?.requires_confirmation) {
        console.log('[CHECKOUT] 🔔 Recent order detected:', result.recent_order);
        console.log('[CHECKOUT] 🔔 Showing confirmation modal');
        const recentOrder = result.recent_order || {};
        setRecentOrderInfo({
          id: recentOrder.id || '',
          order_number: recentOrder.order_number || '',
          tracking_token: recentOrder.tracking_token || '',
          tracking_link: recentOrder.tracking_link || (recentOrder.tracking_token ? `${window.location.origin}/track/${recentOrder.tracking_token}` : ''),
          status: recentOrder.status || '',
          created_at: recentOrder.created_at || '',
          total: recentOrder.total || 0,
          message: result.message || ''
        });
        setPendingPayload(payload);
        setPendingFormData(data);
        setShowRecentOrderModal(true);
        // Unlock UI — user needs to interact with modal
        submittingRef.current = false;
        setIsSubmitting(false);
        return;
      }
      if (!response.ok) {
        const errorMsg = result?.error || result?.message || `Error del servidor (${response.status})`;
        console.error('[CHECKOUT] create-order failed:', errorMsg);
        throw new Error(errorMsg);
      }
      const actualDeliveryFee = data.delivery_type === 'delivery' ? data.external_delivery_fee ?? deliveryFee : 0;
      const orderTotal = subtotal + actualDeliveryFee;
      await handleOrderSuccess(result, data, actualDeliveryFee, orderTotal, restaurantIdNum);
    } catch (error: any) {
      console.error('[CHECKOUT] Order creation error:', error);
      setHardFailureMessage(error?.message || 'No pudimos procesar tu pedido. Inténtalo de nuevo.');
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };
  // ─── Handle force new order (user confirmed) ──────────────
  const handleForceNewOrder = async () => {
    if (!pendingPayload || !pendingFormData || !cart.restaurantId) return;
    console.log('[CHECKOUT] 🔔 User confirmed force_new_order — resending');
    setIsResending(true);
    setShowRecentOrderModal(false);
    setIsSubmitting(true);
    submittingRef.current = true;
    setSubmitError(null);
    try {
      const restaurantIdNum = Number(cart.restaurantId);
      // Generate a FRESH order_intent_id for the forced new order
      const freshIntentId = generateFreshOrderIntentId(restaurantIdNum);
      console.log('[CHECKOUT] 🔔 Fresh order_intent_id for force_new_order:', freshIntentId);
      const forcedPayload = {
        ...pendingPayload,
        order_intent_id: freshIntentId,
        force_new_order: true
      };
      console.log('[CHECKOUT] 🔔 force_new_order resend triggered');
      const {
        response,
        result
      } = await callCreateOrder(forcedPayload);
      console.log('[CHECKOUT] 🔔 Second create-order response received:', result);
      if (!response.ok) {
        const errorMsg = result?.error || result?.message || `Error del servidor (${response.status})`;
        console.error('[CHECKOUT] force_new_order failed:', errorMsg);
        throw new Error(errorMsg);
      }
      const actualDeliveryFee = pendingFormData.delivery_type === 'delivery' ? pendingFormData.external_delivery_fee ?? deliveryFee : 0;
      const orderTotal = subtotal + actualDeliveryFee;
      await handleOrderSuccess(result, pendingFormData, actualDeliveryFee, orderTotal, restaurantIdNum);
    } catch (error: any) {
      console.error('[CHECKOUT] force_new_order error:', error);
      setHardFailureMessage(error?.message || 'No pudimos procesar tu pedido. Inténtalo de nuevo.');
      submittingRef.current = false;
      setIsSubmitting(false);
    } finally {
      setIsResending(false);
      setPendingPayload(null);
      setPendingFormData(null);
      setRecentOrderInfo(null);
    }
  };
  const handleCancelRecentOrder = () => {
    console.log('[CHECKOUT] 🔔 User cancelled — dismissing recent order modal');
    setShowRecentOrderModal(false);
    setRecentOrderInfo(null);
    setPendingPayload(null);
    setPendingFormData(null);
  };
  if (cart.items.length === 0) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-6750">
        <MarketplaceHeader data-id="element-6751" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-6752">
          <EmptyState icon={ShoppingBag} title="Tu carrito está vacío" description="No puedes proceder al pago sin productos." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-6753" />
        </main>
        <Footer data-id="element-6754" />
      </div>;
  }
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-6755">
      <MarketplaceHeader data-id="element-6756" />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-6757">
        <div className="flex items-center gap-4 mb-8" data-id="element-6758">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-6759">
            <ArrowLeft className="w-6 h-6" data-id="element-6760" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-6761">Finalizar Pedido</h1>
        </div>

        {/* Schedule Closed Banner */}
        {isClosedBySchedule && <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3" data-id="element-6762">
            <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-6763">
              <span className="text-red-600 text-sm font-bold" data-id="element-6764">🔒</span>
            </div>
            <div className="flex-1" data-id="element-6765">
              <p className="text-sm font-semibold text-red-800" data-id="element-6766">
                Restaurante cerrado
              </p>
              <p className="text-sm text-red-600 mt-1" data-id="element-6767">
                {scheduleStatus.nextOpeningHint ? `${scheduleStatus.nextOpeningHint}. No podemos recibir tu pedido en este momento.` : 'No podemos recibir pedidos fuera del horario de atención.'}
              </p>
            </div>
          </div>}

        {/* Error Banner */}
        {submitError && <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3" data-id="element-6768">
            <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-6769">
              <span className="text-red-600 text-sm font-bold" data-id="element-6770">!</span>
            </div>
            <div className="flex-1" data-id="element-6771">
              <p className="text-sm font-semibold text-red-800" data-id="element-6772">
                No pudimos procesar tu pedido
              </p>
              <p className="text-sm text-red-600 mt-1" data-id="element-6773">{submitError}</p>
            </div>
            <button onClick={() => setSubmitError(null)} className="text-red-400 hover:text-red-600 text-sm font-bold shrink-0" data-id="element-6774">
              ✕
            </button>
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-6775">
          {/* Main Form Area */}
          <div className="lg:col-span-8 order-2 lg:order-1" data-id="element-6776">
            <CheckoutForm restaurant={restaurant} settings={settings} onSubmit={handleSubmit} isLoading={isSubmitting} initialCustomer={savedCustomer} onCrackPolloDeliveryFeeChange={setCrackPolloDeliveryFee} onExternalDeliveryChange={setExternalDelivery} orderTotal={total} data-id="element-6777" />
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-4 order-1 lg:order-2" data-id="element-6778">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sticky top-24" data-id="element-6779">
              <div className="mb-6 pb-4 border-b border-gray-100" data-id="element-6780">
                <div className="flex items-center gap-3 mb-2" data-id="element-6781">
                  <Store className="w-5 h-5 text-gray-500" data-id="element-6782" />
                  <span className="font-bold text-gray-900" data-id="element-6783">
                    {cart.restaurantName}
                  </span>
                </div>
                <p className="text-xs text-gray-500" data-id="element-6784">
                  Tu pedido será enviado directamente al WhatsApp de este
                  restaurante.
                </p>
              </div>

              <h3 className="font-bold text-gray-900 mb-4" data-id="element-6785">
                Resumen del Pedido
              </h3>

              <div className="space-y-4 mb-6 max-h-[40vh] overflow-y-auto pr-2" data-id="element-6786">
                {cart.items.map(item => {
                const optionsTotal = item.selected_options.reduce((sum, opt) => sum + opt.price_delta, 0);
                const unitPrice = item.unit_price_snapshot + optionsTotal;
                const lineTotal = unitPrice * item.quantity;
                return <div key={item.id} className="flex justify-between text-sm" data-id="element-6787">
                      <div className="flex gap-2" data-id="element-6788">
                        <span className="font-medium text-gray-900" data-id="element-6789">
                          {item.quantity}x
                        </span>
                        <div data-id="element-6790">
                          <p className="text-gray-900" data-id="element-6791">
                            {item.item_name_snapshot}
                          </p>
                          {item.selected_options.length > 0 && <p className="text-xs text-gray-500 mt-0.5" data-id="element-6792">
                              {aggregateQuantityOptions(item.selected_options).map(o => o.qty && o.qty > 1 ? `${o.option_name} x ${o.qty}` : o.option_name).join(', ')}
                            </p>}
                        </div>
                      </div>
                      <span className="font-medium text-gray-900" data-id="element-6793">
                        {formatCurrency(lineTotal)}
                      </span>
                    </div>;
              })}
              </div>

              <div className="space-y-3 text-sm text-gray-600 mb-6 border-t border-gray-100 pt-4" data-id="element-6794">
                <div className="flex justify-between" data-id="element-6795">
                  <span data-id="element-6796">
                    {usesExternalDelivery || isEmilius ? 'Subtotal comida' : 'Subtotal'}
                  </span>
                  <span className="font-medium text-gray-900" data-id="element-6797">
                    {formatCurrency(subtotal + (discount?.amount ?? 0))}
                  </span>
                </div>
                {discount && <div className="flex justify-between text-emerald-600 font-medium" data-id="element-6798">
                    <span data-id="element-6799">{discount.label}</span>
                    <span data-id="element-6800">-{formatCurrency(discount.amount)}</span>
                  </div>}
                {isEmiliusDeliveryConfirmationPending ? <div className="pt-3 border-t border-amber-200" data-id="element-6801">
                    <p className="font-bold text-amber-900" data-id="element-6802">
                      Total y costo de delivery pendientes de confirmación
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-amber-800" data-id="element-6803">
                      Enviá el pedido por WhatsApp para que la pizzería confirme
                      cobertura y costo.
                    </p>
                  </div> : <>
                    {deliveryFee > 0 && <div className="flex justify-between text-brand-amber" data-id="element-6804">
                        <span data-id="element-6805">
                          {usesExternalDelivery ? 'Delivery MS Express' : isEmilius ? 'Express Emilius' : 'Costo de envío'}
                        </span>
                        <span className="font-medium" data-id="element-6806">
                          {formatCurrency(deliveryFee)}
                        </span>
                      </div>}
                    <div className="pt-3 border-t border-gray-100 flex justify-between text-lg font-bold text-gray-900" data-id="element-6807">
                      <span data-id="element-6808">
                        {usesExternalDelivery || isEmilius ? 'Total a pagar' : 'Total'}
                      </span>
                      <span data-id="element-6809">{formatCurrency(total)}</span>
                    </div>
                  </>}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-6810" />

      {hardFailureMessage && <div className="fixed inset-0 z-[105] flex items-center justify-center p-4" data-id="element-6811">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setHardFailureMessage(null)} data-id="element-6812" />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-id="element-6813">
            {/* Header */}
            <div className="px-6 pt-6 pb-4" data-id="element-6814">
              <div className="flex items-center gap-3 mb-3" data-id="element-6815">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0" data-id="element-6816">
                  <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-6817" />
                </div>
                <h3 className="text-lg font-bold text-gray-900" data-id="element-6818">
                  No pudimos procesar tu pedido
                </h3>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-6819">
                No se registró ningún pedido y no se realizó ningún cobro. Por
                favor, contacta al restaurante vía WhatsApp para asistencia.
              </p>
              <details className="mb-4" data-id="element-6820">
                <summary className="cursor-pointer select-none font-medium text-gray-700" data-id="element-6821">
                  Mostrar detalles técnicos
                </summary>
                <p className="mt-2 text-xs font-mono text-gray-600 whitespace-pre-wrap" data-id="element-6822">
                  {hardFailureMessage}
                </p>
              </details>
              {restaurant?.whatsapp_number ? <a href={`https://wa.me/${restaurant.whatsapp_number.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola, necesito asistencia con mi pedido en ${restaurant.name}`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1ebe59] transition-colors" data-id="element-6823">
                  WhatsApp
                </a> : <p className="text-sm text-gray-600" data-id="element-6824">
                  El número de WhatsApp del restaurante no está disponible.
                </p>}
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex justify-end" data-id="element-6825">
              <button onClick={() => setHardFailureMessage(null)} className="h-11 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors px-6" data-id="element-6826">
                Cerrar
              </button>
            </div>
          </div>
        </div>}

      {/* ═══ Recent Order Confirmation Modal ═══ */}
      {showRecentOrderModal && recentOrderInfo && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-6827">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleCancelRecentOrder} data-id="element-6828" />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-id="element-6829">
            {/* Header */}
            <div className="px-6 pt-6 pb-4" data-id="element-6830">
              <div className="flex items-center gap-3 mb-3" data-id="element-6831">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0" data-id="element-6832">
                  <AlertTriangle className="w-5 h-5 text-amber-600" data-id="element-6833" />
                </div>
                <h3 className="text-lg font-bold text-gray-900" data-id="element-6834">
                  Pedido reciente detectado
                </h3>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-6835">
                Acabas de hacer el pedido{' '}
                <span className="font-bold text-gray-900" data-id="element-6836">
                  #{recentOrderInfo.order_number}
                </span>
                . ¿Deseas realizar un pedido nuevo?
              </p>
            </div>

            {/* Tracking link */}
            {recentOrderInfo.tracking_link && <div className="px-6 pb-4" data-id="element-6837">
                <a href={recentOrderInfo.tracking_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline" data-id="element-6838">
                  <ExternalLink className="w-4 h-4" data-id="element-6839" />
                  Ver seguimiento del pedido anterior
                </a>
              </div>}

            {/* Actions */}
            <div className="px-6 pb-6 flex flex-col gap-2" data-id="element-6840">
              <button onClick={handleForceNewOrder} disabled={isResending} className="w-full h-11 rounded-xl bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6841">
                {isResending ? 'Procesando...' : 'Sí, realizar pedido nuevo'}
              </button>

              <div className="flex gap-2" data-id="element-6842">
                {recentOrderInfo.tracking_link && <a href={recentOrderInfo.tracking_link} target="_blank" rel="noreferrer" className="flex-1 h-11 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors flex items-center justify-center gap-1.5" data-id="element-6843">
                    <ExternalLink className="w-3.5 h-3.5" data-id="element-6844" />
                    Ver seguimiento
                  </a>}
                <button onClick={handleCancelRecentOrder} className="flex-1 h-11 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors" data-id="element-6845">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}